import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestDeviceService } from 'src/app/service/rest/device/rest-device.service';
import { RestPlantService } from 'src/app/service/rest/plant/rest-plant.service';
import { RestSerialService } from 'src/app/service/rest/serial/rest-serial.service';
import { LastSearchedService } from 'src/app/service/utils/last-searched.service';
import { PageService } from 'src/app/service/utils/page.service';
import { TranslateFirebaseService } from 'src/app/service/utils/translate-firebase.service';
import { AgendaRepositoryService } from '../agenda/agenda-repository.service';
import { PlantRepositoryService } from '../plant/plant-repository.service';
import { UserService } from '../user/user.service';
import {
  Device,
  DeviceNameSerial,
  SubscribeSerialNumber,
  SubscribeSerialNumbers,
  UnsubscribeSerialNumber,
} from './device.model';
import { DeviceService } from './device.service';

export const USER_DEVICES_STORAGE_KEY = 'Upservice-user-devices';

@Injectable()
export class DeviceRepositoryService {
  private _userDevices: Device[] = [];
  private _plantDevices: Device[] = [];

  public userDevicesLoaded: boolean = false;
  plantDevicesLoaded: boolean = false;
  plantId: string;

  constructor(
    private deviceRest: RestDeviceService,
    private pageService: PageService,
    private plantRest: RestPlantService,
    private deviceService: DeviceService,
    private userService: UserService,
    private agendaRepo: AgendaRepositoryService,
    private plantRepository: PlantRepositoryService,
    private serialRest: RestSerialService,
    private translate: TranslateFirebaseService,
    private lastSearchedService: LastSearchedService
  ) {}

  getUserDevices() {
    this.loadUserDevices();
  }

  get userDevices(): Device[] {
    let data: Device[] = JSON.parse(
      sessionStorage.getItem(USER_DEVICES_STORAGE_KEY)
    );
    if (this._userDevices.length == 0 && !this.userDevicesLoaded && data) {
      data.forEach((device, index) => {
        this.userDevicesLoaded = true;
        if (device.creationDate)
          device.creationDate = new Date(device.creationDate?.toString());
        if (device.activationDate)
          device.activationDate = new Date(device.activationDate?.toString());
      });

      this.userDevices = data;
      return data;
    } else return this._userDevices;
  }

  set userDevices(devices: Device[]) {
    sessionStorage.setItem(USER_DEVICES_STORAGE_KEY, JSON.stringify(devices));
    this._userDevices = devices;
  }

  get plantDevices(): Device[] {
    return this._plantDevices;
  }

  set plantDevices(devices: Device[]) {
    this._plantDevices = devices;
  }

  set device(device: Device) {
    this.deviceService.device = device;
  }

  get device(): Device {
    return this.deviceService.device;
  }

  getPlantDevices(id: string): Device[] {
    if (!this.plantDevicesLoaded && id != this.plantId)
      this.loadPlantDevices(id);

    return this.plantDevices;
  }

  private loadUserDevices() {
    this.deviceRest.getUserDeviceList().subscribe(
      (result: Device[]) => {
        this.userDevicesLoaded = true;
        if (result)
          result.forEach((device, index) => {
            if (device.creationDate)
              device.creationDate = new Date(device.creationDate?.toString());
            if (device.activationDate)
              device.activationDate = new Date(
                device.activationDate?.toString()
              );
            //TODO da rimettere se i documenti di garanzia non sono fatti
            //if (index == 0) device.certificatePath = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
          });
        this.userDevices = result;

        let subscribeSerialNumbers = new SubscribeSerialNumbers();

        subscribeSerialNumbers.serialNumberIds = this.userDevices.map(
          (device: Device) => device.serialNumberId
        );
        subscribeSerialNumbers.userId = this.userService.upsUser.id;
        subscribeSerialNumbers.clear = true;

        this.deviceRest
          .subscribeSerialNumbers(subscribeSerialNumbers)
          .subscribe(
            () => {
              console.log(
                'Serials subscribe for user ID:' + this.userService.upsUser.id
              );
              this.agendaRepo.getAllAgendaByUser();
              this.agendaRepo.getUserNotificationCount();
            },
            (error) => {
              console.log(error);
            }
          );
      },
      (error) => {
        this.userDevicesLoaded = false;
        console.log(error);
      }
    );
  }

  private loadPlantDevices(id: string) {
    this.plantId = id;

    this.plantRest.getDeviceListByPlantId(id).subscribe(
      (result) => {
        this.plantDevices = result;
      },
      (error) => {
        console.log(error.status);
      }
    );
  }

  createDevice(): Observable<Device> {
    return this.deviceRest.createDevice(this.device);
  }

  subscribeSerialNumber(serialNumberId: number): Observable<any> {
    let subscribeSerialNumber: SubscribeSerialNumber =
      new SubscribeSerialNumber();
    subscribeSerialNumber.serialNumberId = serialNumberId;
    subscribeSerialNumber.userIds = [this.userService.upsUser.id];

    return this.deviceRest.subscribeSerialNumber(subscribeSerialNumber);
  }

  archiveDevice(id: string): Observable<any> {
    return this.deviceRest.deleteDevice(id);
  }

  unsubscribeSerialNumber(serialNumberId: number) {
    let unsubscribeSerial = new UnsubscribeSerialNumber();
    unsubscribeSerial.serialNumberId = serialNumberId;
    unsubscribeSerial.userIds = [this.userService.upsUser.id];

    this.deviceRest.unsubscribeSerialNumber(unsubscribeSerial).subscribe(
      () => {
        console.log('Unsubscribed Serial with ID:' + serialNumberId);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  getUserDeviceById(id: string) {
    if (this.userDevicesLoaded) {
      let device: Device = this.userDevices.find(
        (device: Device) => device.id == id
      );
      this.device = device;
    } else
      this.deviceRest.getDevice(id).subscribe((result) => {
        this.device = result;
      });
  }

  getPlantDeviceById(id: string) {
    if (this.plantDevicesLoaded) {
      let device: Device = this.plantDevices.find(
        (device: Device) => device.id == id
      );
      this.deviceService.device = device;
    } else
      this.deviceRest.getDevice(id).subscribe((result) => {
        this.deviceService.device = result;
      });
  }

  getDevicesPlants(ascending: boolean) {
    if (ascending)
      return this.userDevices
        .map((device) => device.plantId)
        .filter((ele, index, array) => array.indexOf(ele) == index)
        .sort((a, b) => (a > b ? 1 : -1));
    else
      return this.userDevices
        .map((device) => device.plantId)
        .filter((ele, index, array) => array.indexOf(ele) == index)
        .sort((a, b) => (a > b ? -1 : 1));
  }

  getDevicesByPlant(plantId: string): Device[] {
    return this.userDevices.filter(
      (device) => plantId == undefined || device.plantId == plantId
    );
  }

  getDevicesTotByPlant(plantId: string): number {
    return this.userDevices.filter(
      (device) => plantId == undefined || device.plantId == plantId
    ).length;
  }

  getDevicesByDate(date: Date): Device[] {
    return this.userDevices.filter(
      (device) =>
        date == undefined ||
        device.activationDate.setHours(0, 0, 0) == date.setHours(0, 0, 0)
    );
  }

  getDevicesDates(order: boolean) {
    //IF order == true newest to oldest, else oldest to newest
    if (order)
      return this.userDevices
        .map((device) => device.activationDate)
        .filter(
          (ele, index, array) =>
            array.findIndex(
              (i) => i.setHours(0, 0, 0) === ele.setHours(0, 0, 0)
            ) == index
        )
        .sort((a, b) => (a > b ? -1 : 1));
    else
      return this.userDevices
        .map((device) => device.activationDate)
        .filter(
          (ele, index, array) =>
            array.findIndex(
              (i) => i.setHours(0, 0, 0) === ele.setHours(0, 0, 0)
            ) == index
        )
        .sort((a, b) => (a > b ? 1 : -1));
  }

  getDevicesChars(ascending: boolean) {
    if (ascending)
      return this.userDevices
        .map((device) => device.name.toLowerCase().charAt(0))
        .filter((ele, index, array) => array.indexOf(ele) == index)
        .sort((a, b) => (a > b ? 1 : -1));
    else
      return this.userDevices
        .map((device) => device.name.toLowerCase().charAt(0))
        .filter((ele, index, array) => array.indexOf(ele) == index)
        .sort((a, b) => (a > b ? -1 : 1));
  }

  getDevicesByChar(char: string): Device[] {
    return this.userDevices.filter(
      (device) =>
        char == undefined ||
        device.name.toLowerCase().charAt(0) == char.toLowerCase()
    );
  }

  updateDevice() {
    this.deviceRest.updateDevice(this.device).subscribe(
      (result: Device) => {
        this.pageService.openSnackBar(
          this.translate.getTranslation('update_device_success'),
          'Ok'
        );
        const index: number = this.userDevices
          .map((device) => device.id)
          .indexOf(this.device.id);
        this.deviceService.device = this.device;
        this.userDevices[index] = this.device;
        sessionStorage.setItem(
          USER_DEVICES_STORAGE_KEY,
          JSON.stringify(this.userDevices)
        );
      },
      (error) => {
        this.pageService.openErrorSnackBar(
          this.translate.getTranslation('update_device_failed'),
          'Ok'
        );
        console.log(error.status);
      }
    );
  }

  reset() {
    this.userDevices = [];
    this.plantDevices = [];
    sessionStorage.removeItem(USER_DEVICES_STORAGE_KEY);
  }

  clearFromHistory(id: string) {
    this.lastSearchedService.clearFromHistory(id);
  }

  downloadDocument(documentId: number): Observable<any> {
    return this.deviceRest.downloadDocument(documentId);
  }

  getUserDevicesSerials(): DeviceNameSerial[] {
    return this.userDevices.map(
      (device) => new DeviceNameSerial(device.serialNumber, device.name)
    );
  }

  getDeviceNameBySerialNumber(serialNumber: string): string {
    let index: number = this.userDevices.findIndex(
      (element) =>
        element.serialNumber.toUpperCase() == serialNumber.toUpperCase()
    );
    return this.userDevices[index].name;
  }

  getSerialNumberByDeviceName(deviceName: string): string {
    let index: number = this.userDevices.findIndex(
      (element) =>
        element.name.toUpperCase() == deviceName.toUpperCase()
    );
    return this.userDevices[index].serialNumber;
  }

  getPlantName(serialNumber: string): string {
    let deviceIndex: number = this.userDevices.findIndex(
      (element) =>
        element.serialNumber.toUpperCase() == serialNumber.toUpperCase()
    );
    let plantIndex: number = this.plantRepository.plants.findIndex(
      (element) => element.id == this.userDevices[deviceIndex].plantId
    );
    return this.plantRepository.plants[plantIndex].name;
  }

  getPlantNameByDeviceName(deviceName: string): string {
    let deviceIndex: number = this.userDevices.findIndex(
      (element) =>
        element.name.toUpperCase() == deviceName.toUpperCase()
    );
    let plantIndex: number = this.plantRepository.plants.findIndex(
      (element) => element.id == this.userDevices[deviceIndex].plantId
    );
    return this.plantRepository.plants[plantIndex].name;
  }
}
